// Vista previa en vivo del plan por capítulos (PE-C1): el texto del capítulo abierto o del plan completo, en el mismo
// orden que el PDF (armarVistaPorCapitulos, que reagrupa armarVistaPrevia sin cambiar su contenido). Se recalcula con
// un pequeño retraso para no rehacerlo en cada tecla. En el celular va debajo del capítulo, plegada, con un botón.

import React, { useEffect, useMemo, useState } from 'react';
import { armarVistaPorCapitulos, CapituloVista, IdVista, TITULO_CONTINUIDAD_PESTANA } from '../../lib/pe/pe-capitulos';
import { DatosVista, MARCA_DE_AGUA, SeccionVista } from '../../lib/pe/pe-vista-previa';
import { Semaforo } from './ComponentesPE';

export const RETRASO_VISTA_MS = 400;

export function useRetrasado<T>(valor: T, ms = RETRASO_VISTA_MS): T {
  const [x, setX] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setX(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return x;
}

function Lineas({ lineas }: { lineas: SeccionVista['lineas'] }) {
  return (
    <ul className="pe2-vista-lista">
      {lineas.map((l, i) => (
        <li key={i} className={l.pendiente ? 'pe2-pendiente' : l.estado === 'por_validar' ? 'pe2-resaltado' : undefined}>
          {l.estado && l.estado !== 'informativo' && <Semaforo estado={l.estado} />} {l.texto}
        </li>
      ))}
    </ul>
  );
}

function Capitulo({ c }: { c: CapituloVista }) {
  return (
    <section className="pe2-vp-capitulo" aria-label={`${c.numero}. ${c.titulo}`}>
      <h4>{c.numero}. {c.titulo}</h4>
      {c.subsecciones.map(s => (
        <div key={s.numero}>
          <b className="pe2-vp-sub">{s.numero} {s.titulo}{s.porValidar > 0 ? ` · ${s.porValidar} por validar` : ''}</b>
          <Lineas lineas={s.lineas} />
        </div>
      ))}
    </section>
  );
}

export default function VistaPreviaCapitulos({ datos, vista }: { datos: DatosVista; vista: IdVista }) {
  const [modo, setModo] = useState<'capitulo' | 'completo'>('capitulo');
  const [abierta, setAbierta] = useState(false);       // solo cuenta en pantallas angostas (CSS)
  const retrasados = useRetrasado(datos);
  const v = useMemo(() => armarVistaPorCapitulos(retrasados), [retrasados]);
  const actual = typeof vista === 'number' ? v.capitulos[vista - 1]
    : vista === 'continuidad' ? v.capitulos.find(c => c.titulo === TITULO_CONTINUIDAD_PESTANA) ?? null : null;
  const completo = modo === 'completo' || vista === 'revision';

  return (
    <aside className={`pe2-vp${abierta ? ' abierta' : ''}`} aria-label="Vista previa del plan">
      <button type="button" className="pe2-btn pe2-vp-boton" aria-expanded={abierta} onClick={() => setAbierta(a => !a)}>
        {abierta ? 'Ocultar vista previa' : 'Ver vista previa'}
      </button>
      <div className="pe2-vp-cuerpo">
        <div className="pe2-cabecera">
          <h3>Vista previa</h3>
          <label className="pe2-campo pe2-vp-modo">
            <span className="pe2-sr">Mostrar</span>
            <select aria-label="Mostrar en la vista previa" value={completo ? 'completo' : 'capitulo'} disabled={vista === 'revision'}
              onChange={e => setModo(e.target.value as 'capitulo' | 'completo')}>
              <option value="capitulo">Capítulo actual</option>
              <option value="completo">Plan completo</option>
            </select>
          </label>
        </div>
        <p className="pe2-nota" role="note">{MARCA_DE_AGUA}. Mismo orden que el PDF.</p>
        {completo ? (
          <>
            <section className="pe2-vp-capitulo" aria-label="Portada"><h4>Portada</h4><Lineas lineas={v.portada.lineas} /></section>
            {v.capitulos.map(c => <Capitulo key={c.numero} c={c} />)}
            <section className="pe2-vp-capitulo" aria-label="Firmas"><h4>Firmas</h4><Lineas lineas={v.firmas.lineas} /></section>
          </>
        ) : actual ? <Capitulo c={actual} /> : (
          <p className="pe2-nota">Sin datos de continuidad del negocio ni de póliza: el PDF no incluye esta parte.</p>
        )}
      </div>
    </aside>
  );
}
